import type { HTMLAttributes, ReactNode, JSX } from 'react'

import { clsx } from 'clsx'

import { Flex, Skeleton, SkeletonText } from '@buffer-mono/popcorn'

import styles from './EngagementItem.module.css'

export type EngagementVariant = 'root' | 'context' | 'replyForm' | 'lastReply'

type EngagementItemElement = 'article' | 'div' | 'section' | 'aside'

interface EngagementItemProps extends Omit<HTMLAttributes<HTMLElement>, 'as'> {
  /**
   * The HTML element type to render. Defaults to 'article'.
   */
  as?: EngagementItemElement
  /**
   * Defines the visual/layout flavour of the card. Defaults to `root`
   * (top-level engagement).
   */
  variant?: EngagementVariant
  /**
   * Node rendered in the left column — usually the avatar + connector line.
   */
  avatar: ReactNode
  /**
   * Children rendered in the right (content) column.
   */
  children: ReactNode
}

/**
 * Presentational two-column layout for a single engagement row (comment or
 * mention). The avatar occupies the left column spanning both rows; `children`
 * fill the right content column. `variant` selects the visual treatment for
 * top-level vs context vs reply-form vs last-reply rows.
 */
export const EngagementItem = ({
  as: Component = 'article',
  variant = 'root',
  avatar,
  children,
  className,
  ...props
}: EngagementItemProps): JSX.Element => {
  return (
    <Component
      data-testid="engagement-item"
      {...props}
      className={clsx(styles.card, styles[variant], className)}
    >
      <aside className={styles.avatar}>{avatar}</aside>
      <div className={styles.content}>{children}</div>
    </Component>
  )
}

EngagementItem.displayName = 'EngagementItem'

export const EngagementItemSkeleton = (): JSX.Element => {
  return (
    <div className={clsx(styles.card, styles.root)}>
      <aside className={styles.avatar}>
        <Skeleton rounded width={36} height={36} />
      </aside>
      <div className={styles.content}>
        <header>
          <Flex align="center" gap="xs" justify="between">
            <Flex align="center" gap="xs">
              <SkeletonText lines={1} width="6ch" />
              <SkeletonText lines={1} width="4ch" />
            </Flex>
            <Skeleton rounded width={24} height={24} />
          </Flex>
        </header>
        <SkeletonText lines={3} width="100%" />
      </div>
    </div>
  )
}

EngagementItemSkeleton.displayName = 'EngagementItemSkeleton'
